import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Breadcrumbs,
    Link,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Search as SearchIcon,
    People as PeopleIcon,
    Assignment as AssignmentIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import toastr from 'toastr';
import * as LegacyViewerService from '../../../services/clinicPortalServices/legacyViewerService';
import PatientDetail from './PatientDetail';
import { formatNumber } from '../../../services/common/util';

const LegacyViewer = () => {
    const [loading, setLoading] = useState(false);
    const [patients, setPatients] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [pendingQuery, setPendingQuery] = useState('');
    const [stats, setStats] = useState({ patientCount: 0, requisitionCount: 0, pdfCount: 0 });
    const [pagination, setPagination] = useState({ page: 1, limit: 25, totalCount: 0, totalPages: 0 });
    const [sortField, setSortField] = useState('lastName');
    const [sortOrder, setSortOrder] = useState('asc');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const gridRef = useRef(null);

    // Load stats on mount
    useEffect(() => {
        loadStats();
        loadPatients('', 1, 25, 'lastName', 'asc');
    }, []);

    const loadStats = async () => {
        try {
            const response = await LegacyViewerService.getLegacyStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    };

    const loadPatients = async (query, page, limit, sortFld, sortOrd) => {
        try {
            setLoading(true);
            const response = await LegacyViewerService.searchLegacyPatients(query, page, limit, sortFld, sortOrd);
            if (response.success) {
                setPatients(response.data);
                setPagination(response.pagination);
            }
        } catch (error) {
            toastr.error('Failed to load patients');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = useCallback(() => {
        setSearchQuery(pendingQuery);
        loadPatients(pendingQuery, 1, pagination.limit, sortField, sortOrder);
    }, [pendingQuery, pagination.limit, sortField, sortOrder]);

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setPendingQuery('');
        setSearchQuery('');
        loadPatients('', 1, pagination.limit, sortField, sortOrder);
    };

    const handlePageChange = (newPage) => {
        loadPatients(searchQuery, newPage, pagination.limit, sortField, sortOrder);
    };

    const handleSortChanged = (event) => {
        const columnState = event.api.getColumnState();
        const sortedCol = columnState.find(c => c.sort);
        if (sortedCol) {
            const newSortField = sortedCol.colId;
            const newSortOrder = sortedCol.sort;
            setSortField(newSortField);
            setSortOrder(newSortOrder);
            loadPatients(searchQuery, 1, pagination.limit, newSortField, newSortOrder);
        }
    };

    const handleRowClicked = async (event) => {
        const patientId = event.data._id;
        try {
            setDetailLoading(true);
            const response = await LegacyViewerService.getLegacyPatient(patientId);
            if (response.success) {
                setSelectedPatient(response.data);
            }
        } catch (error) {
            toastr.error('Failed to load patient details');
            console.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    const columnDefs = [
        { headerName: 'Last Name', field: 'lastName', sortable: true, flex: 1, minWidth: 120 },
        { headerName: 'First Name', field: 'firstName', sortable: true, flex: 1, minWidth: 120 },
        {
            headerName: 'DOB',
            field: 'dob',
            sortable: true,
            width: 110,
            valueFormatter: (params) => {
                if (!params.value) return '';
                const d = new Date(params.value + 'T00:00:00');
                return isNaN(d.getTime()) ? params.value : d.toLocaleDateString();
            }
        },
        { headerName: 'MRN', field: 'mrn', sortable: true, width: 115 },
        { headerName: 'Sex', field: 'sex', sortable: true, width: 65 },
        { headerName: 'Phone', field: 'phone', sortable: false, width: 130 },
        { headerName: 'Requisition IDs', field: 'requisitionIds', sortable: false, flex: 2, minWidth: 180, tooltipField: 'requisitionIds' },
        { headerName: 'Sample IDs', field: 'sampleIds', sortable: false, flex: 2, minWidth: 150, tooltipField: 'sampleIds' },
        { headerName: '# Reqs', field: 'requisitionCount', sortable: true, width: 80, type: 'numericColumn' },
        { headerName: '# PDFs', field: 'pdfCount', sortable: true, width: 80, type: 'numericColumn' }
    ];

    const defaultColDef = {
        resizable: true,
        filter: false
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box mb={3}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" href="/clinic/dashboard">
                        Clinic Portal
                    </Link>
                    <Typography color="text.primary">Legacy Patient Records</Typography>
                </Breadcrumbs>

                <Typography variant="h4" component="h1" sx={{ mt: 2 }}>
                    Legacy Patient Records
                </Typography>
            </Box>

            {/* Stats Badges */}
            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">
                                Patients
                            </Typography>
                            <Typography variant="h5">{formatNumber(stats.patientCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">
                                Requisitions
                            </Typography>
                            <Typography variant="h5">{formatNumber(stats.requisitionCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <PictureAsPdfIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">
                                PDF Reports
                            </Typography>
                            <Typography variant="h5">{formatNumber(stats.pdfCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search Bar */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box display="flex" gap={2} alignItems="center">
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search by name, MRN, or DOB (MM/DD/YYYY)..."
                        value={pendingQuery}
                        onChange={(e) => setPendingQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: pendingQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={loading}>
                        Search
                    </Button>
                </Box>
                {searchQuery && (
                    <Box mt={1}>
                        <Chip
                            label={`Search: "${searchQuery}"`}
                            onDelete={handleClearSearch}
                            size="small"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {formatNumber(pagination.totalCount)} result{pagination.totalCount !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Patient Grid */}
            <Paper sx={{ width: '100%', height: 'calc(100vh - 450px)', minHeight: 400 }}>
                {loading && patients.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress />
                    </Box>
                ) : patients.length === 0 && !loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={4}>
                        <Alert severity="info">
                            {searchQuery
                                ? 'No patients match your search.'
                                : 'No legacy patients found. Please run the ETL import first.'}
                        </Alert>
                    </Box>
                ) : (
                    <div className="ag-theme-balham" style={{ width: '100%', height: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={patients}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            onRowClicked={handleRowClicked}
                            onSortChanged={handleSortChanged}
                            rowSelection="single"
                            suppressCellFocus={true}
                            overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading patients...</span>'
                            getRowId={(params) => params.data._id}
                            rowStyle={{ cursor: 'pointer' }}
                        />
                    </div>
                )}
            </Paper>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={2}>
                    <Button
                        size="small"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                    >
                        Previous
                    </Button>
                    <Typography variant="body2">
                        Page {pagination.page} of {pagination.totalPages}
                    </Typography>
                    <Button
                        size="small"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                    >
                        Next
                    </Button>
                </Box>
            )}

            {/* Patient Detail Dialog */}
            {selectedPatient && (
                <PatientDetail
                    patient={selectedPatient}
                    onClose={() => setSelectedPatient(null)}
                />
            )}

            {/* Loading overlay for detail fetch */}
            {detailLoading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        zIndex: 9999
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default LegacyViewer;
